import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';

import update from 'immutability-helper';
import R from 'ramda';

import {
  Switch,
  Route,
  useRouteMatch,
  useLocation,
} from 'react-router-dom';

import SurveySection from '_templates/SurveySection';
import ActivityIndicator from '_atoms/ActivityIndicator/ActivityIndicator';

import { attemptGetSurveyAnswers, attemptUpsertAnswers } from '_thunks/answers'
import { attemptSubmitSurvey } from '_thunks/surveys';

import { filterVisibleComponents, filterVisibleRoutes, getAnswer, checkSurvey } from '_utils/survey';
import useKeyPress from '_hooks/useKeyPress';

import ThankYou from '_pages/ThankYou';

const FORWARD = 1;
const BACKWARD = -1;

const enterBlocklist = [
  "TextArea"
]

const errorMap = {
  "missing fields": "null"
}

export default function SurveyRouter({ survey }) {
  const dispatch = useDispatch();

  const { path, url } = useRouteMatch();

  const { answers, user } = useSelector(R.pick(['answers', 'user']));

  const base_uri = `${path}/${survey.base_uri}`;
  const routes = survey.routes.sort((a, b) => a.order - b.order);
  const { pathname } = useLocation();

  const [loading, setLoading] = useState(true);

  const [curPos, setCurPos] = useState(routes.findIndex(r => r.uri == location.href.split('/').reverse()[0]) + 1);
  const [curRoute, setCurRoute] = useState(routes.find(r => r.uri == location.href.split('/').reverse()[0]));

  const [saving, setSaving] = useState(false);

  const [error, setError] = useState(false);

  const savingTimeoutManager = { //TODO: I don't think this works properly
    timeoutId: null,
    startTimeout: function () {
      this.timeoutId = setTimeout(() => {
        setSaving(false)
      }, 5000);
    },
    clearTimeout: function() {
      if(this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
    }
  };

  useEffect(() => {
    console.log('path: ', path)
    console.log(user);

    if (!R.isEmpty(user)) {
      dispatch(attemptGetSurveyAnswers(survey.id))
        .then(() => {
          setLoading(false);
        })
    } else {
      setLoading(false);

      dispatch(push(`/auth`));
    }
  }, [])

  useEffect(() => {
    console.log(curPos);
    console.log(curRoute);
    console.log(survey);
    console.log(answers);
  }, [curPos])

  useEffect(() => {
    const uri = pathname.split("/").reverse()[0];
    const curPosition = R.findIndex(R.propEq("uri", uri), routes);

    setCurPos(curPosition + 1);
    setCurRoute(routes[curPosition]);
  }, [pathname]);

  const validateRoute = (steps) => {
    if(process.env.IS_DEV) {
      return {  valid: true, message: "Testing", invalid: [] }
    }

    let valid = true, message = "", invalid = [], overrides = [];

    for (const component of filterVisibleComponents(answers, curRoute.body.components)) {
      let answer = getAnswer(answers, component.id);

      if (component.override_for && answer && answer.length > 0) {
        const index = R.findIndex(R.propEq('id', component.override_for), invalid);

        overrides.push(component.override_for);

        if (index != -1) {
          invalid = update(invalid, { $splice: [[index, 1]] })
        }

        valid = invalid.length == 0;
      }

      if (steps > BACKWARD && (component.required && !overrides.includes(component.id)) && (!answer || answer == "" || answer == [])) {
        valid = false;
        message = "missing fields";
        invalid.push({ id: component.id, reason: "required" });

        continue;
      }

      for (validator of component.validators) {
        //TODO: add validators
      }
    };

    return { valid, message, invalid }
  }

  //TODO: async?, saving?
  const attemptSubmitRoute = () => {
    savingTimeoutManager.clearTimeout();

    const current_questions = curRoute.body.components.map(c => { return { qID: c.id, validators: c.validators, ...c } });
    const q_ids = R.pluck('qID', current_questions);
    const current_answers = answers.filter(a => a.sID == survey.id && q_ids.includes(a.qID))

    setSaving(true);

    dispatch(attemptUpsertAnswers({
      survey_id: survey.id,
      answers: current_answers.map(answer => {
        console.log(answer);
        return { question_id: answer.qID, answer: answer.answer, encrypted_field: answer.encrypted_field ?? false }
      })
    }));

    savingTimeoutManager.startTimeout();
  }

  /**
   * Navigates to nexPos
   * @param {*} nextPos 
   *
   * TODO: handle invalid route
   */

  const navigate = (nextPos) => {
    console.log(routes);
    const nextRoute = routes[nextPos - 1]; //offset routes array (starts at 0)
    setCurPos(nextPos);
    setCurRoute(nextRoute);
    dispatch(push(`${base_uri}/${nextRoute.uri}`));
  }

  /**
   * Finds next route
   * Direction should be: "1" for forward or "-1" for backward
   * 
   * Returns -1 if array out of bounds.
   * 
   * @param {*} position
   * @param {*} direction
   */

  const findNextRoute = (position, direction) => {
    let nextPos = position + direction;

    if (nextPos > routes.length || nextPos < 1) {
      return -1;
    }

    if (routes[nextPos - 1].conditional) {
      let conditions_met = checkSurvey(answers, routes[nextPos - 1].conditional_requirements);

      if (!conditions_met) {
        return findNextRoute(nextPos, direction)
      }
    }

    return nextPos;
  }

  const move = steps => {
    const { valid, message, invalid } = validateRoute(steps);

    if (valid) {
      setError(false);

      attemptSubmitRoute();

      const nextPos = findNextRoute(curPos, steps)

      if (nextPos != -1) {
        navigate(nextPos);
      } else {
        if (steps < 0) { //Back from beginning
          //TODO: Handle back from beginning
        } else { //Submit Survey
          dispatch(attemptSubmitSurvey({survey_id: survey.id}))
          dispatch(push(`${base_uri}/thank-you`));
        }
      }
    } else {
      console.log("Invalid: ", invalid);
      console.log(message);
      setError(errorMap[message]);
    }
  }

  const blockEnter = () => {
    for (const c of filterVisibleComponents(answers, curRoute.body.components)) {
      if (enterBlocklist.includes(c.type)) return true;
    }
    return false;
  }

  useKeyPress('Enter', () => { if (!blockEnter()) move(FORWARD); })

  return (
    loading ? <div className="full_wrapper"><ActivityIndicator /></div> : <Switch>
      <Route exact path={path} key={path}>
        <div style={{ paddingTop: '25px' }}>
          {R.isEmpty(user) && <>No User Object</>}
          {!R.isEmpty(user) && <>Logged In</>}
        </div>
      </Route>
      <Route exact path={`${base_uri}/thank-you`} component={ThankYou} key={"thank-you"}/>
      <Route exact path={base_uri} key={survey.base_uri}>
        <>{survey.survey_name}</>
      </Route>
      {routes.map((route, index, key) => (
        <Route path={`${base_uri}/${route.uri}`} key={key}>
          <SurveySection
            survey_id={survey.id}
            survey_options={survey.options}
            survey_meta={survey.meta}
            uri={route.uri}
            title={route.title}
            body={route.body}

            position={curPos}
            forward={() => move(FORWARD)}
            backward={() => move(BACKWARD)}

            error={error}
            saved={saving}

            index={index}
          />
        </Route>
      ))}
      <Route path={`${base_uri}/*`}>
        <>{survey.survey_name /** TODO: router */}</>
      </Route>
    </Switch>
  );
}